import { useCallback, useEffect, useState } from "react";
import { CalendarDays, Clock, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LEAVE_FORM = {
  leave_code: "CL",
  from_date: "",
  to_date: "",
  reason: "",
  contact_during_leave: "",
};

export default function ShiftsLeave() {
  const [shifts, setShifts] = useState([]);
  const [types, setTypes] = useState([]);
  const [applications, setApplications] = useState([]);
  const [form, setForm] = useState(LEAVE_FORM);
  const [showForm, setShowForm] = useState(false);
  const { user } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [controls, setControls] = useState({
    employee_id: "",
    shift_code: "GENERAL",
    effective_from: "",
    leave_code: "CL",
    days: "",
    effective_date: "",
    reason: "",
  });
  const canManage = ["admin", "director", "hr"].includes(user?.role);

  const load = useCallback(async () => {
    const [shiftsRes, typesRes, applicationsRes] = await Promise.all([
      api.get("/hrms/shifts"),
      api.get("/hrms/leave/types"),
      api.get("/hrms/leave/applications"),
    ]);
    setShifts(shiftsRes.data);
    setTypes(typesRes.data);
    setApplications(applicationsRes.data);
    const approvalRes = await api.get("/hrms/leave/applications", {
      params: { view: "approvals" },
    });
    setApprovals(approvalRes.data);
    if (canManage) {
      const [employeeRes, correctionRes] = await Promise.all([
        api.get("/hrms/employees", { params: { status: "active" } }),
        api.get("/hrms/attendance/corrections"),
      ]);
      setEmployees(employeeRes.data.items);
      setCorrections(correctionRes.data);
    }
  }, [canManage]);

  useEffect(() => {
    load().catch(() => toast.error("Unable to load shifts and leave."));
  }, [load]);

  const submitLeave = async () => {
    if (!form.from_date || !form.to_date || !form.reason) {
      toast.error("Complete the leave dates and reason.");
      return;
    }
    try {
      await api.post("/hrms/leave/applications", form);
      toast.success("Leave application submitted for approval.");
      setForm(LEAVE_FORM);
      setShowForm(false);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to submit leave application.");
    }
  };

  const decideLeave = async (application_id, decision) => {
    const remarks = decision === "rejected" ? window.prompt("Reason for rejection") : "";
    if (decision === "rejected" && !remarks?.trim()) return;
    try {
      await api.post(`/hrms/leave/applications/${application_id}/decision`, { decision, remarks });
      toast.success(`Leave application ${decision}.`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update leave application.");
    }
  };

  const creditLeave = async () => {
    if (!controls.employee_id || !controls.days || !controls.effective_date || !controls.reason) {
      toast.error("Complete the leave credit details.");
      return;
    }
    try {
      await api.post("/hrms/leave/credits", {
        employee_id: controls.employee_id,
        leave_code: controls.leave_code,
        days: Number(controls.days),
        effective_date: controls.effective_date,
        reason: controls.reason,
      });
      toast.success("Leave balance credited.");
      setControls({ ...controls, days: "", reason: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to credit leave.");
    }
  };

  const assignShift = async () => {
    if (!controls.employee_id || !controls.effective_from) {
      toast.error("Select an employee and shift effective date.");
      return;
    }
    try {
      await api.post("/hrms/shift-assignments", {
        employee_id: controls.employee_id,
        shift_code: controls.shift_code,
        effective_from: controls.effective_from,
      });
      toast.success("Shift assigned.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to assign shift.");
    }
  };

  const decideCorrection = async (adjustment_id, decision) => {
    try {
      await api.post(`/hrms/attendance/corrections/${adjustment_id}/decision`, { decision });
      toast.success(`Attendance correction ${decision}.`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update correction.");
    }
  };

  return (
    <div className="space-y-5" data-testid="hrms-shifts-leave-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Time off</p>
          <h1 className="mt-1 text-4xl font-bold">Shift & Leave Management</h1>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="hrms-apply-leave-button">
          <Plus className="mr-2 h-4 w-4" /> Apply leave
        </Button>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="card-soft p-5" data-testid="hrms-shift-list">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold">Approved shifts</h2>
          </div>
          <div className="mt-4 space-y-3">
            {shifts.map((shift) => (
              <div
                key={shift.shift_code}
                className={[
                  "flex items-center justify-between border-b",
                  "border-border/60 pb-3 last:border-0",
                ].join(" ")}
              >
                <div>
                  <p className="font-semibold">{shift.name}</p>
                  <p className="font-mono text-xs text-muted-foreground">{shift.shift_code}</p>
                </div>
                <p className="metric text-sm">{shift.start_time} – {shift.end_time}</p>
              </div>
            ))}
          </div>
        </section>
        <section className="card-soft p-5" data-testid="hrms-leave-applications">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold">My leave applications</h2>
          </div>
          <div className="mt-4 space-y-3">
            {applications.map((application) => (
              <div
                key={application.application_id}
                className="border-b border-border/60 pb-3 last:border-0"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold">
                    {application.leave_code} · {application.days} day(s)
                  </p>
                  <span
                    className="tm-chip tm-progress"
                    data-testid={`hrms-leave-status-${application.application_id}`}
                  >
                    {application.status.replace(/_/g, " ")}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {application.from_date} to {application.to_date}
                </p>
              </div>
            ))}
            {!applications.length && (
              <p className="text-sm text-muted-foreground" data-testid="hrms-leave-empty">
                No leave applications yet.
              </p>
            )}
          </div>
        </section>
      </div>
      {showForm && (
        <section className="card-soft p-5" data-testid="hrms-leave-form">
          <h2 className="text-xl font-bold">Apply for leave</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <Label>Leave type</Label>
              <select
                className={[
                  "mt-1 h-10 w-full rounded-md border border-input",
                  "bg-background px-3 text-sm",
                ].join(" ")}
                value={form.leave_code}
                onChange={(event) => setForm({ ...form, leave_code: event.target.value })}
                data-testid="hrms-leave-type"
              >
                {types.map((type) => (
                  <option key={type.leave_code} value={type.leave_code}>{type.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Reason</Label>
              <Input
                value={form.reason}
                onChange={(event) => setForm({ ...form, reason: event.target.value })}
                data-testid="hrms-leave-reason"
              />
            </div>
            <div>
              <Label>From date</Label>
              <Input
                type="date"
                value={form.from_date}
                onChange={(event) => setForm({ ...form, from_date: event.target.value })}
                data-testid="hrms-leave-from-date"
              />
            </div>
            <div>
              <Label>To date</Label>
              <Input
                type="date"
                value={form.to_date}
                onChange={(event) => setForm({ ...form, to_date: event.target.value })}
                data-testid="hrms-leave-to-date"
              />
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowForm(false)}
              data-testid="hrms-leave-cancel-button"
            >
              Cancel
            </Button>
            <Button onClick={submitLeave} data-testid="hrms-leave-submit-button">
              Submit for approval
            </Button>
          </div>
        </section>
      )}
      {(canManage || approvals.length > 0) && (
        <section className="grid gap-4 xl:grid-cols-3" data-testid="hrms-manager-controls">
          <div className="card-soft p-5">
            <h2 className="text-xl font-bold">Leave approval inbox</h2>
            <div className="mt-4 space-y-3">
              {approvals.map((application) => (
                <div key={application.application_id} className="border-b border-border/60 pb-3">
                  <p className="font-semibold">{application.employee_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {application.leave_code} · {application.from_date} to {application.to_date}
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => decideLeave(application.application_id, "approved")}
                      data-testid={`hrms-leave-approve-${application.application_id}`}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => decideLeave(application.application_id, "rejected")}
                      data-testid={`hrms-leave-reject-${application.application_id}`}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
              {!approvals.length && (
                <p className="text-sm text-muted-foreground">No leave approvals assigned.</p>
              )}
            </div>
          </div>
          {canManage && (
            <>
          <div className="card-soft p-5">
            <h2 className="text-xl font-bold">Assign shift</h2>
            <div className="mt-4 space-y-3">
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={controls.employee_id}
                onChange={(event) => {
                  setControls({ ...controls, employee_id: event.target.value });
                }}
                data-testid="hrms-control-employee"
              >
                <option value="">Select employee</option>
                {employees.map((employee) => (
                  <option key={employee.employee_id} value={employee.employee_id}>
                    {employee.full_name}
                  </option>
                ))}
              </select>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={controls.shift_code}
                onChange={(event) => {
                  setControls({ ...controls, shift_code: event.target.value });
                }}
                data-testid="hrms-control-shift"
              >
                {shifts.map((shift) => (
                  <option key={shift.shift_code}>{shift.shift_code}</option>
                ))}
              </select>
              <Input
                type="date"
                value={controls.effective_from}
                onChange={(event) => {
                  setControls({ ...controls, effective_from: event.target.value });
                }}
                data-testid="hrms-control-shift-date"
              />
              <Button
                className="w-full"
                onClick={assignShift}
                data-testid="hrms-control-assign-shift"
              >
                Assign shift
              </Button>
            </div>
          </div>
          <div className="card-soft p-5">
            <h2 className="text-xl font-bold">Credit leave balance</h2>
            <div className="mt-4 space-y-3">
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={controls.leave_code}
                onChange={(event) => {
                  setControls({ ...controls, leave_code: event.target.value });
                }}
                data-testid="hrms-control-leave-type"
              >
                {types.map((type) => (
                  <option key={type.leave_code}>{type.leave_code}</option>
                ))}
              </select>
              <Input
                type="number"
                min="0.5"
                value={controls.days}
                onChange={(event) => {
                  setControls({ ...controls, days: event.target.value });
                }}
                placeholder="Days to credit"
                data-testid="hrms-control-credit-days"
              />
              <Input
                type="date"
                value={controls.effective_date}
                onChange={(event) => {
                  setControls({ ...controls, effective_date: event.target.value });
                }}
                data-testid="hrms-control-credit-date"
              />
              <Input
                value={controls.reason}
                onChange={(event) => {
                  setControls({ ...controls, reason: event.target.value });
                }}
                placeholder="Reason"
                data-testid="hrms-control-credit-reason"
              />
              <Button
                className="w-full"
                onClick={creditLeave}
                data-testid="hrms-control-credit-leave"
              >
                Credit leave
              </Button>
            </div>
          </div>
          <div className="card-soft p-5 xl:col-span-3" data-testid="hrms-corrections-inbox">
            <h2 className="text-xl font-bold">Attendance corrections</h2>
            <div className="mt-4 space-y-3">
              {corrections.map((correction) => (
                <div
                  key={correction.adjustment_id}
                  className={[
                    "flex flex-wrap items-center justify-between gap-3",
                    "border-b border-border/60 pb-3",
                  ].join(" ")}
                >
                  <p className="text-sm">
                    <span className="font-semibold">{correction.employee_name}</span>
                    <span className="text-muted-foreground"> · {correction.reason}</span>
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => decideCorrection(correction.adjustment_id, "approved")}
                      data-testid={`hrms-correction-approve-${correction.adjustment_id}`}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => decideCorrection(correction.adjustment_id, "rejected")}
                      data-testid={`hrms-correction-reject-${correction.adjustment_id}`}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
              {!corrections.length && (
                <p className="text-sm text-muted-foreground">No attendance corrections pending.</p>
              )}
            </div>
          </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}